import c from 'classnames';
import React from 'react';

import { EvaluationColor } from '@repo/types';
import { RangeBar, Typography } from '@repo/ui';
import { userValueFormat } from 'helpers';
import { EvaluationPillFromColor } from 'views/Results/Insights/SubMetricGraphs/SubMetricGraphs';
import { MainFinding } from 'views/Results/Summary/SummaryCards/NoteCard/useMainFindings';

import styles from './RetestPriorityCard.module.css';

// Taxa names are italicised by convention; metabolites are not.
const ITALIC_TAXA_LEVELS = ['species', 'genus'];

interface RetestPriorityCardProps {
  metric: MainFinding;
}

export const RetestPriorityCard: React.FC<RetestPriorityCardProps> = ({
  metric,
}) => {
  const dataType = metric.gvh?.data_type || 'number';
  const ranges = metric.gvh?.gut_vaginal_health_version_ranges;

  const isItalic = ITALIC_TAXA_LEVELS.includes(metric.gvh?.taxa_level ?? '');

  const valueDisplay = userValueFormat(
    metric.user_value,
    dataType,
    metric.user_string,
  );

  const rangeBarRanges = (ranges ?? []).map(range => ({
    range_lower: range.range_lower,
    range_upper: range.range_upper,
    evaluation_color: range.evaluation_color as EvaluationColor,
  }));

  // Same guard as the metric detail page and the PDF report.
  const canRenderBar =
    dataType !== 'string' &&
    !!metric.gvh?.range_bar_display &&
    !!ranges?.length;

  return (
    <div className={styles.card}>
      <div className={styles.left}>
        <EvaluationPillFromColor
          evaluation_color={metric.evaluation_color}
          showIconOnly
        />
        <Typography
          variant='body-s'
          italic={isItalic}
          className={styles.name}
        >
          {metric.gvh?.name}
        </Typography>
      </div>

      <div className={c(styles.right, !canRenderBar && styles.valueOnly)}>
        {canRenderBar ? (
          <RangeBar
            ranges={rangeBarRanges}
            value={metric.user_value}
            min={metric.gvh?.scale_bar_min || undefined}
            max={metric.gvh?.scale_bar_max || undefined}
            showValue
            valueDisplay={valueDisplay}
            variant='result'
          />
        ) : (
          <Typography variant='body-s'>{valueDisplay}</Typography>
        )}
      </div>
    </div>
  );
};

export default RetestPriorityCard;
